import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Services API
export const servicesAPI = {
  getAll: async () => {
    const response = await api.get('/services');
    return response.data;
  },
  
  getBySlug: async (slug) => {
    const response = await api.get(`/services/${slug}`);
    return response.data;
  },
};

// Cases API
export const casesAPI = {
  getAll: async () => {
    const response = await api.get('/cases');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/cases/${id}`);
    return response.data;
  },
};

// Stylist Profile API (Marketing)
export const stylistProfileAPI = {
  getProfile: async () => {
    const response = await api.get('/stylist');
    return response.data;
  },
};

// Stylist Cabinet API
export const stylistAPI = {
  // Auth
  login: async (password) => {
    const response = await api.post('/stylist/auth/login', { password });
    return response.data;
  },
  
  // Clients
  getAllClients: async () => {
    const response = await api.get('/stylist/clients');
    return response.data;
  },
  
  createClient: async (clientData) => {
    const response = await api.post('/stylist/clients', clientData);
    return response.data;
  },
  
  updateClient: async (clientId, clientData) => {
    const response = await api.put(`/stylist/clients/${clientId}`, clientData);
    return response.data;
  },
  
  getClientDetails: async (clientId) => {
    const response = await api.get(`/stylist/clients/${clientId}`);
    return response.data;
  },
  
  generateMagicLink: async (clientId) => {
    const response = await api.post(`/stylist/clients/${clientId}/magic-link`);
    return response.data;
  },
  
  // Subscription
  createSubscription: async (clientId, subscriptionData) => {
    const response = await api.post(`/stylist/subscription/${clientId}`, subscriptionData);
    return response.data;
  },
  
  addActionsToSubscription: async (clientId, count, note = '') => {
    const response = await api.patch(`/stylist/subscription/${clientId}/add-actions?count=${count}&note=${encodeURIComponent(note)}`);
    return response.data;
  },
  
  // Requests Stats
  getRequestsStats: async () => {
    const response = await api.get(`/stylist/requests-stats`);
    return response.data;
  },
  
  // Wardrobe
  getWardrobe: async (clientId) => {
    const response = await api.get(`/client/wardrobe/${clientId}`);
    return response.data;
  },
  
  addWardrobeItem: async (clientId, itemData) => {
    const response = await api.post(`/stylist/wardrobe/${clientId}`, itemData);
    return response.data;
  },
  
  updateWardrobeItem: async (itemId, itemData) => {
    const response = await api.put(`/stylist/wardrobe/${itemId}`, itemData);
    return response.data;
  },
  
  deleteWardrobeItem: async (itemId) => {
    const response = await api.delete(`/stylist/wardrobe/${itemId}`);
    return response.data;
  },
  
  // Looks
  getLooks: async (clientId) => {
    const response = await api.get(`/client/looks/${clientId}`);
    return response.data;
  },
  
  createLook: async (clientId, lookData) => {
    const response = await api.post(`/stylist/looks/${clientId}`, lookData);
    return response.data;
  },
  
  updateLook: async (lookId, lookData) => {
    const response = await api.put(`/stylist/looks/${lookId}`, lookData);
    return response.data;
  },
  
  deleteLook: async (lookId) => {
    const response = await api.delete(`/stylist/looks/${lookId}`);
    return response.data;
  },
  
  // Capsules
  getCapsules: async (clientId, status = null) => {
    let url = `/stylist/capsules/${clientId}`;
    if (status) url += `?status=${status}`;
    const response = await api.get(url);
    return response.data;
  },
  
  getCapsuleDetail: async (capsuleId) => {
    const response = await api.get(`/stylist/capsule/${capsuleId}`);
    return response.data;
  },
  
  createCapsule: async (clientId, capsuleData) => {
    const response = await api.post(`/stylist/capsules/${clientId}`, capsuleData);
    return response.data;
  },
  
  updateCapsule: async (capsuleId, capsuleData) => {
    const response = await api.put(`/stylist/capsule/${capsuleId}`, capsuleData);
    return response.data;
  },
  
  deleteCapsule: async (capsuleId) => {
    const response = await api.delete(`/stylist/capsule/${capsuleId}`);
    return response.data;
  },
  
  publishCapsule: async (capsuleId) => {
    const response = await api.patch(`/stylist/capsule/${capsuleId}/publish`);
    return response.data;
  },
  
  archiveCapsule: async (capsuleId) => {
    const response = await api.patch(`/stylist/capsule/${capsuleId}/archive`);
    return response.data;
  },
  
  setCapsuleDraft: async (capsuleId) => {
    const response = await api.patch(`/stylist/capsule/${capsuleId}/draft`);
    return response.data;
  },
  
  addItemsToCapsule: async (capsuleId, itemIds) => {
    const response = await api.post(`/stylist/capsule/${capsuleId}/items`, itemIds);
    return response.data;
  },
  
  removeItemFromCapsule: async (capsuleId, itemId) => {
    const response = await api.delete(`/stylist/capsule/${capsuleId}/items/${itemId}`);
    return response.data;
  },
  
  addLooksToCapsule: async (capsuleId, lookIds) => {
    const response = await api.post(`/stylist/capsule/${capsuleId}/looks`, lookIds);
    return response.data;
  },
  
  removeLookFromCapsule: async (capsuleId, lookId) => {
    const response = await api.delete(`/stylist/capsule/${capsuleId}/looks/${lookId}`);
    return response.data;
  },
  
  reorderLooksInCapsule: async (capsuleId, lookIds) => {
    const response = await api.put(`/stylist/capsule/${capsuleId}/looks/order`, lookIds);
    return response.data;
  },
  
  // Recommendations
  getRecommendations: async (clientId) => {
    const response = await api.get(`/client/recommendations/${clientId}`);
    return response.data;
  },
  
  addRecommendation: async (clientId, recommendationData) => {
    const response = await api.post(`/stylist/recommendations/${clientId}`, recommendationData);
    return response.data;
  },
  
  updateRecommendation: async (recommendationId, updateData) => {
    const response = await api.put(`/stylist/recommendations/${recommendationId}`, updateData);
    return response.data;
  },
  
  deleteRecommendation: async (recommendationId) => {
    const response = await api.delete(`/stylist/recommendations/${recommendationId}`);
    return response.data;
  },
  
  // Client Requests
  getClientRequests: async (clientId) => {
    const response = await api.get(`/stylist/requests/${clientId}`);
    return response.data;
  },
  
  createClientRequest: async (clientId, requestData) => {
    const response = await api.post(`/stylist/requests?client_id=${clientId}`, requestData);
    return response.data;
  },
  
  getRequestDetail: async (requestId) => {
    const response = await api.get(`/stylist/requests/detail/${requestId}`);
    return response.data;
  },
  
  updateRequestStatus: async (requestId, status) => {
    const response = await api.patch(`/stylist/requests/${requestId}/status`, { status });
    return response.data;
  },
  
  deleteRequest: async (requestId) => {
    const response = await api.delete(`/stylist/requests/${requestId}`);
    return response.data;
  },
  
  // Image Upload
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/stylist/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
};

// Contact Request API
export const contactAPI = {
  submit: async (data) => {
    const response = await api.post('/contact', data);
    return response.data;
  },
};

// Client Cabinet API
export const clientAPI = {
  // Auth
  requestMagicLink: async (emailOrTelegram) => {
    const response = await api.post('/client/auth/magic-link', { email_or_telegram: emailOrTelegram });
    return response.data;
  },
  
  verifyToken: async (token) => {
    const response = await api.get(`/client/auth/verify?token=${token}`);
    return response.data;
  },
  
  // Profile
  getProfile: async (clientId) => {
    const response = await api.get(`/client/profile/${clientId}`);
    return response.data;
  },
  
  // Dashboard
  getDashboard: async (clientId) => {
    const response = await api.get(`/client/dashboard/${clientId}`);
    return response.data;
  },
  
  // Subscription
  getSubscription: async (clientId) => {
    const response = await api.get(`/client/subscription/${clientId}`);
    return response.data;
  },
  
  // Wardrobe
  getWardrobe: async (clientId, filters = {}) => {
    let url = `/client/wardrobe/${clientId}`;
    const params = new URLSearchParams();
    if (filters.itemType) params.append('item_type', filters.itemType);
    if (filters.season) params.append('season', filters.season);
    if (params.toString()) url += `?${params.toString()}`;
    const response = await api.get(url);
    return response.data;
  },
  
  // Looks
  getLooks: async (clientId) => {
    const response = await api.get(`/client/looks/${clientId}`);
    return response.data;
  },
  
  // Capsules
  getCapsules: async (clientId, season = null) => {
    let url = `/client/capsules/${clientId}`;
    if (season) url += `?season=${season}`;
    const response = await api.get(url);
    return response.data;
  },
  
  getCapsuleDetail: async (capsuleId) => {
    const response = await api.get(`/client/capsule/${capsuleId}`);
    return response.data;
  },
  
  // Recommendations
  getRecommendations: async (clientId) => {
    const response = await api.get(`/client/recommendations/${clientId}`);
    return response.data;
  },
  
  // Requests
  getRequests: async (clientId) => {
    const response = await api.get(`/client/requests/${clientId}`);
    return response.data;
  },
  
  createRequest: async (clientId, requestData) => {
    const response = await api.post(`/client/requests/${clientId}`, requestData);
    return response.data;
  },
  
  getRequestDetail: async (clientId, requestId) => {
    const response = await api.get(`/client/requests/${clientId}/${requestId}`);
    return response.data;
  },
};

export default api;
